// src/features/tutor/TutoringSession.js
import React, { useState, useEffect, useMemo } from 'react';
import PreviewPanel from './PreviewPanel';
import {
  UNIT_QUESTIONS,
  OVERALL_QUESTION,
  OVERALL_OPTIONS,
  RATING_OPTIONS,
  RATING_BY_VALUE,
} from '../learnerImpact/tutorFeedbackOptions';

const MAX_UNIT_MINUTES = 30;
const TEAL = '#19a4a0';
const RED = '#dc2626';

const formatTime = (minutes) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const formatSeconds = (seconds) => {
  if (seconds < 60) return `${seconds}s`;
  return formatTime(Math.round(seconds / 60));
};

const newSittingId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

// Compact Weak / Good / Excellent / Not observed row, with the full question
// as a hint underneath the label. Nothing is pre-selected.
function RatingRow({ question, value, onChange }) {
  return (
    <div style={{ marginBottom: '10px' }}>
      <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#111827' }}>{question.label}</div>
      <div style={{ fontSize: '0.75rem', color: '#6b7280', margin: '2px 0 6px' }}>{question.question}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {RATING_OPTIONS.map(opt => {
          const selected = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              style={{
                padding: '6px 12px', borderRadius: '999px', fontSize: '0.8rem', cursor: 'pointer',
                border: `1px solid ${selected ? TEAL : '#d1d5db'}`,
                background: selected ? TEAL : 'white',
                color: selected ? 'white' : (opt.score == null ? '#6b7280' : '#374151'),
                fontWeight: selected ? 600 : 400,
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TutoringSession runs one lesson = one tutor session. Units are guidelines:
// the tutor covers as many as fit and closes with "End session" on any unit.
//
// Feedback (version 2):
//   per unit, per learner    → Participation + Preparation, or 🚫 Not present
//   per session, per learner → Overall (⭐ 👍 🔄) on "Closing the session"
//
// Each unit is saved the moment its answers are complete. Overall is asked
// when the session closes (End session, the last unit, or Back) for every
// learner present in at least one saved unit.
//
// Props:
//   lessonTitle, units, startIndex, learners ([{ id, name }])
//   onSaveUnit(unit, presenceMap, durationMinutes, extra) → Promise
//       presenceMap = learnerId -> 'present' | 'absent'
//       extra = { participationMap, preparationMap, durationSeconds, sittingId }
//   onSaveSessionOverall(overallMap, log, sittingId) → Promise
//   onFinishLesson(log) — leave after at least one unit was saved
//   onBack()            — leave when nothing was saved
// ---------------------------------------------------------------------------
function TutoringSession({
  lessonTitle, units, startIndex = 0, learners,
  onSaveUnit, onSaveSessionOverall, onFinishLesson, onBack
}) {
  const [sittingId] = useState(newSittingId);
  const [currentIndex, setCurrentIndex] = useState(startIndex);
  const [unitStartedAt, setUnitStartedAt] = useState(() => Date.now());
  const [elapsedTime, setElapsedTime] = useState(0);

  // Current unit's answers, per learner
  const [absentMap, setAbsentMap] = useState({});        // learnerId -> true
  const [participationMap, setParticipationMap] = useState({});
  const [preparationMap, setPreparationMap] = useState({});

  const [showPreview, setShowPreview] = useState(false);
  const [showExitWarning, setShowExitWarning] = useState(false);
  const [exitMode, setExitMode] = useState('back'); // 'back' | 'end'
  const [autoStopped, setAutoStopped] = useState(false);
  const [saving, setSaving] = useState(false);
  const [completedLog, setCompletedLog] = useState([]); // units saved in this session
  const [showRecap, setShowRecap] = useState(false);

  // Closing step: Overall per learner. `afterClosing` = 'recap' | 'leave'.
  const [showClosing, setShowClosing] = useState(false);
  const [afterClosing, setAfterClosing] = useState('recap');
  const [canReturnToUnit, setCanReturnToUnit] = useState(false);
  const [overallMap, setOverallMap] = useState({});

  const currentUnit = units[currentIndex];
  const totalUnits = units.length;
  const isLastUnit = currentIndex >= totalUnits - 1;
  const onUnitScreen = !showRecap && !showClosing;

  // A learner is done for this unit when marked Not present, or when both
  // Participation and Preparation are answered.
  const learnerDone = (id) =>
    !!absentMap[id] || (!!participationMap[id] && !!preparationMap[id]);
  const allFeedbackGiven = learners.length > 0 && learners.every(l => learnerDone(l.id));
  const anyAnswerGiven =
    Object.keys(absentMap).length > 0 ||
    Object.keys(participationMap).length > 0 ||
    Object.keys(preparationMap).length > 0;

  // Learners present for at least one saved unit — the ones asked for Overall.
  const presentLearners = (log) =>
    learners.filter(l => log.some(e => e.presenceMap[l.id] === 'present'));
  const closingLearners = useMemo(
    () => presentLearners(completedLog),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [completedLog, learners]
  );
  const allOverallGiven = closingLearners.every(l => overallMap[l.id]);

  // New unit → clear its answers.
  useEffect(() => {
    setElapsedTime(0);
    setAutoStopped(false);
    setAbsentMap({});
    setParticipationMap({});
    setPreparationMap({});
    setShowPreview(false);
  }, [unitStartedAt]);

  // Per-unit timer — only runs while the tutor is on a unit screen.
  useEffect(() => {
    if (!onUnitScreen) return undefined;
    const startTime = unitStartedAt;
    const timer = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000 / 60);
      if (elapsed >= MAX_UNIT_MINUTES) {
        setElapsedTime(MAX_UNIT_MINUTES);
        setAutoStopped(true);
        clearInterval(timer);
        setShowPreview(false);
        alert(`⏰ This unit automatically stopped at ${MAX_UNIT_MINUTES} minutes. Please provide feedback for each learner.`);
      } else {
        setElapsedTime(elapsed);
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [unitStartedAt, onUnitScreen]);

  // Seconds spent on the current unit, capped at the auto-stop limit.
  const unitSeconds = () =>
    Math.min(MAX_UNIT_MINUTES * 60, Math.max(0, Math.floor((Date.now() - unitStartedAt) / 1000)));

  const toggleAbsent = (learnerId) => {
    setAbsentMap(prev => {
      const next = { ...prev };
      if (next[learnerId]) delete next[learnerId];
      else next[learnerId] = true;
      return next;
    });
  };

  const setRating = (key, learnerId, value) => {
    const setter = key === 'participation' ? setParticipationMap : setPreparationMap;
    setter(prev => ({ ...prev, [learnerId]: value }));
  };

  // Saves the current unit; returns the updated log.
  const saveCurrentUnit = async () => {
    const durationSeconds = unitSeconds();
    const durationMinutes = Math.floor(durationSeconds / 60);
    const presenceMap = {};
    const partForSave = {};
    const prepForSave = {};
    learners.forEach(l => {
      if (absentMap[l.id]) {
        presenceMap[l.id] = 'absent';
      } else {
        presenceMap[l.id] = 'present';
        partForSave[l.id] = participationMap[l.id];
        prepForSave[l.id] = preparationMap[l.id];
      }
    });
    await onSaveUnit(currentUnit, presenceMap, durationMinutes, {
      participationMap: partForSave,
      preparationMap: prepForSave,
      durationSeconds,
      sittingId,
    });
    const entry = {
      unit: currentUnit,
      presenceMap,
      participationMap: partForSave,
      preparationMap: prepForSave,
      durationMinutes,
      durationSeconds,
    };
    const newLog = [...completedLog, entry];
    setCompletedLog(newLog);
    return newLog;
  };

  // Closing the session: ask Overall first if anyone was present for a saved
  // unit. `returnable` = the current unit is still unsaved, so "Back to unit"
  // is safe.
  const leave = (log, next = 'leave', returnable = true) => {
    if (presentLearners(log).length > 0) {
      setAfterClosing(next);
      setCanReturnToUnit(returnable);
      setShowPreview(false);
      setShowClosing(true);
      return;
    }
    if (log.length === 0) { onBack(); return; }
    if (next === 'recap') { setShowRecap(true); return; }
    onFinishLesson(log);
  };

  const handleAdvance = async () => {
    if (!allFeedbackGiven) {
      alert('Please answer every question for every learner before continuing.');
      return;
    }
    setSaving(true);
    try {
      const newLog = await saveCurrentUnit();
      if (isLastUnit) {
        leave(newLog, 'recap', false);
      } else {
        setCurrentIndex(i => i + 1);
        setUnitStartedAt(Date.now());
      }
    } catch (error) {
      console.error('Error saving unit feedback:', error);
      alert("Couldn't save this unit's feedback. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // End session — the tutor's deliberate close, available on every unit.
  // A fully answered unit is saved first; a half-answered one asks first.
  const handleEndSession = async () => {
    if (allFeedbackGiven) {
      setSaving(true);
      try {
        const newLog = await saveCurrentUnit();
        leave(newLog, 'recap', false);
      } catch (error) {
        console.error('Error saving unit feedback:', error);
        alert("Couldn't save this unit's feedback. Please try again.");
      } finally {
        setSaving(false);
      }
    } else if (anyAnswerGiven) {
      setExitMode('end');
      setShowExitWarning(true);
    } else {
      leave(completedLog, 'recap', true);
    }
  };

  const handleSaveOverall = async () => {
    if (!allOverallGiven) {
      alert('Please give the overall feedback for every learner.');
      return;
    }
    setSaving(true);
    try {
      const map = {};
      closingLearners.forEach(l => { map[l.id] = overallMap[l.id]; });
      await onSaveSessionOverall(map, completedLog, sittingId);
      setShowClosing(false);
      if (afterClosing === 'recap') setShowRecap(true);
      else onFinishLesson(completedLog);
    } catch (error) {
      console.error('Error saving overall feedback:', error);
      alert("Couldn't save the overall feedback. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleBack = async () => {
    // Recap: everything is saved already, Back = Done.
    if (showRecap) { onFinishLesson(completedLog); return; }
    if (showClosing) {
      if (canReturnToUnit) setShowClosing(false);
      return;
    }
    if (showPreview) { setShowPreview(false); return; }

    if (allFeedbackGiven) {
      // Every answer is in — save this unit rather than lose it, then close.
      setSaving(true);
      try {
        const newLog = await saveCurrentUnit();
        leave(newLog, 'leave', false);
      } catch (error) {
        console.error('Error saving unit feedback:', error);
        alert("Couldn't save this unit's feedback. Please try again.");
      } finally {
        setSaving(false);
      }
    } else if (anyAnswerGiven) {
      setExitMode('back');
      setShowExitWarning(true);
    } else {
      leave(completedLog);
    }
  };

  // Leaving or ending with incomplete answers: this unit is NOT saved.
  // Units already saved in this session stay saved.
  const forceExit = () => {
    setShowExitWarning(false);
    leave(completedLog, exitMode === 'end' ? 'recap' : 'leave', true);
  };

  const totalLoggedSeconds = completedLog.reduce((sum, e) => sum + (e.durationSeconds || 0), 0);

  return (
    <div className="tutoring-session">

      {/* ── Sticky timer bar ── */}
      <div className="session-timer-bar">
        <div className="session-timer-bar-left">
          {!showRecap && !(showClosing && !canReturnToUnit) && (
            <button className="back-button" onClick={handleBack} disabled={saving}>
              ← {showPreview ? 'Back to Feedback' : showClosing ? 'Back to unit' : 'Back'}
            </button>
          )}
          <div className="session-timer-bar-info">
            <span className="session-unit-name">{lessonTitle}</span>
            <span className="session-learner-names">
              {showRecap
                ? 'Recap'
                : showClosing
                  ? 'Closing the session'
                  : `Unit ${currentIndex + 1} of ${totalUnits} · ${currentUnit.title}`}
              {' · '}{learners.map(l => l.name).join(' · ')}
            </span>
          </div>
        </div>

        {onUnitScreen && (
          <div className="session-timer-bar-right">
            <div className="session-timer-compact">
              <span className="session-timer-value" style={{ color: autoStopped ? RED : '#111827' }}>
                {formatTime(elapsedTime)}
              </span>
              <span className="session-timer-status" style={{ color: autoStopped ? RED : '#10b981' }}>
                {autoStopped ? '⏱️ Stopped' : '🔴 Live'}
              </span>
            </div>

            <button
              className={`preview-toggle-button ${showPreview ? 'active' : ''}`}
              onClick={() => setShowPreview(v => !v)}
            >
              {showPreview ? '📋 Show Feedback' : '👁️ Preview Unit'}
            </button>
          </div>
        )}
      </div>

      {/* Lesson progress */}
      <div style={{ height: '4px', backgroundColor: '#e5e7eb' }}>
        <div style={{
          width: `${Math.round(((onUnitScreen ? currentIndex : totalUnits) / totalUnits) * 100)}%`,
          height: '100%', backgroundColor: TEAL, transition: 'width 0.2s',
        }} />
      </div>

      {/* ── Preview view ── */}
      {onUnitScreen && showPreview && (
        <div className="session-preview-area">
          <PreviewPanel lessonId={currentUnit.lesson_id} unitId={currentUnit.id} />
        </div>
      )}

      {/* ── Unit feedback view ── */}
      {onUnitScreen && !showPreview && (
        <div className="session-content">

          {autoStopped && (
            <div style={{
              backgroundColor: '#fef3c7', border: '1px solid #fbbf24',
              borderRadius: '8px', padding: '1rem', marginBottom: '1rem',
              fontSize: '0.875rem', color: '#92400e'
            }}>
              ⚠️ This unit automatically stopped at 30 minutes. Please provide feedback below.
            </div>
          )}

          <div className="feedback-section">
            <h3>Feedback per learner *</h3>
            <div className="learner-feedback-grid">
              {learners.map(learner => {
                const absent = !!absentMap[learner.id];
                return (
                  <div key={learner.id} className="learner-feedback-card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <div className="learner-feedback-name" style={{ marginBottom: 0 }}>{learner.name}</div>
                      <button
                        type="button"
                        onClick={() => toggleAbsent(learner.id)}
                        style={{
                          padding: '4px 10px', borderRadius: '999px', fontSize: '0.75rem', cursor: 'pointer',
                          border: `1px solid ${RED}`,
                          background: absent ? RED : 'white',
                          color: absent ? 'white' : RED,
                          fontWeight: 600,
                        }}
                      >
                        🚫 Not present
                      </button>
                    </div>

                    {absent ? (
                      <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: 0 }}>
                        Marked as not present for this unit. Tap again to undo.
                      </p>
                    ) : (
                      UNIT_QUESTIONS.map(q => (
                        <RatingRow
                          key={q.key}
                          question={q}
                          value={(q.key === 'participation' ? participationMap : preparationMap)[learner.id]}
                          onChange={(v) => setRating(q.key, learner.id, v)}
                        />
                      ))
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <button
            className="mark-covered-button"
            onClick={handleAdvance}
            disabled={!allFeedbackGiven || saving}
            style={{
              opacity: (allFeedbackGiven && !saving) ? 1 : 0.5,
              cursor: (allFeedbackGiven && !saving) ? 'pointer' : 'not-allowed'
            }}
          >
            {saving ? 'Saving…' : isLastUnit ? 'End session' : 'Next unit →'}
          </button>

          {/* Units are guidelines — the tutor can close the session after any unit. */}
          {!isLastUnit && (
            <button
              type="button"
              onClick={handleEndSession}
              disabled={saving}
              style={{
                display: 'block', width: '100%', marginTop: '10px', padding: '10px',
                borderRadius: '8px', border: `1px solid ${TEAL}`, background: 'white',
                color: TEAL, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              ■ End session
            </button>
          )}
          <p style={{ fontSize: '0.75rem', color: '#6b7280', textAlign: 'center', margin: '6px 0 0' }}>
            {isLastUnit
              ? 'This is the last unit of the lesson.'
              : 'You don’t have to cover every unit — end the session whenever it’s over.'}
          </p>
        </div>
      )}

      {/* ── Closing the session — Overall, once per learner ── */}
      {showClosing && (
        <div className="session-content">
          <div style={{ marginBottom: '1rem' }}>
            <h3 style={{ margin: '0 0 4px' }}>Closing the session</h3>
            <p style={{ color: '#6b7280', margin: 0, fontSize: '0.875rem' }}>
              {OVERALL_QUESTION.question}
              {' '}({completedLog.length} unit{completedLog.length !== 1 ? 's' : ''} · {formatSeconds(totalLoggedSeconds)})
            </p>
          </div>

          <div className="learner-feedback-grid">
            {closingLearners.map(learner => (
              <div key={learner.id} className="learner-feedback-card">
                <div className="learner-feedback-name">{learner.name}</div>
                <div className="feedback-options">
                  {Object.entries(OVERALL_OPTIONS).map(([value, opt]) => (
                    <button
                      key={value}
                      className={`feedback-option ${overallMap[learner.id] === value ? 'selected' : ''}`}
                      onClick={() => setOverallMap(prev => ({ ...prev, [learner.id]: value }))}
                    >
                      <span className="feedback-icon">{opt.icon}</span>
                      <span className="feedback-label">{opt.tutorLabel}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <button
            className="mark-covered-button"
            onClick={handleSaveOverall}
            disabled={!allOverallGiven || saving}
            style={{
              opacity: (allOverallGiven && !saving) ? 1 : 0.5,
              cursor: (allOverallGiven && !saving) ? 'pointer' : 'not-allowed'
            }}
          >
            {saving ? 'Saving…' : afterClosing === 'recap' ? 'Save and see recap' : 'Save and leave'}
          </button>
        </div>
      )}

      {/* ── Recap — shown once, when the session is ended ── */}
      {showRecap && (
        <div className="session-content">
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <div style={{ fontSize: '32px' }}>🎉</div>
            <h3 style={{ margin: '8px 0 4px' }}>Session complete</h3>
            <p style={{ color: '#6b7280', margin: 0 }}>
              {lessonTitle} · {completedLog.length} unit{completedLog.length !== 1 ? 's' : ''} covered · {formatSeconds(totalLoggedSeconds)} total
            </p>
          </div>

          {closingLearners.length > 0 && (
            <div style={{
              border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '10px',
              background: '#f0fdfa',
            }}>
              <strong>Overall</strong>
              {closingLearners.map(l => {
                const opt = OVERALL_OPTIONS[overallMap[l.id]];
                return (
                  <div key={l.id} style={{ fontSize: '0.875rem', color: '#374151' }}>
                    {l.name}: {opt ? `${opt.icon} ${opt.label}` : '—'}
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '1.5rem' }}>
            {completedLog.map((entry, i) => (
              <div key={i} style={{
                border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem 1rem',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <strong>{entry.unit.title}</strong>
                  <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>{formatSeconds(entry.durationSeconds || 0)}</span>
                </div>
                {learners.map(l => (
                  <div key={l.id} style={{ fontSize: '0.875rem', color: '#374151' }}>
                    {l.name}:{' '}
                    {entry.presenceMap[l.id] === 'absent'
                      ? '🚫 Not present'
                      : `Participation: ${RATING_BY_VALUE[entry.participationMap[l.id]]?.label || '—'} · Preparation: ${RATING_BY_VALUE[entry.preparationMap[l.id]]?.label || '—'}`}
                  </div>
                ))}
              </div>
            ))}
          </div>

          <button
            className="mark-covered-button"
            onClick={() => onFinishLesson(completedLog)}
          >
            Done
          </button>
        </div>
      )}

      {/* Exit warning — leaving a unit with incomplete answers */}
      {showExitWarning && (
        <div className="complete-modal-overlay">
          <div className="complete-modal">
            <h3>⚠️ Unit Not Complete</h3>
            <p style={{ marginBottom: '1.5rem' }}>
              Not every question is answered for this unit, so it won&apos;t be saved.
              Units you already finished stay saved.
              {exitMode === 'end' && ' Go back to finish it, or end the session without it.'}
            </p>
            <div className="modal-actions">
              <button
                className="cancel-button"
                onClick={() => setShowExitWarning(false)}
                style={{ flex: 1 }}
              >
                Continue
              </button>
              <button
                className="confirm-button"
                onClick={forceExit}
                style={{ flex: 1, backgroundColor: RED }}
              >
                {exitMode === 'end' ? 'End without this unit' : 'Leave anyway'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TutoringSession;